import { initialEvents } from '../data/events';

// Classe d'erreur personnalisée pour les erreurs Mock API
class MockApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'MockApiError';
    this.response = {
      status: status,
      data: { message: message }
    };
  }
}

// Simuler un délai réseau
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Stockage local simulé (remplace la base de données)
let mockFestivals = [...initialEvents];
let nextId = Math.max(...mockFestivals.map(f => f.id)) + 1;
let nextTournamentId = Math.max(
  ...mockFestivals.flatMap(f => f.subEvents.map(t => t.id))
) + 1;

// =============================================
// MOCK FESTIVALS API
// =============================================

export const mockFestivalsAPI = {
  // Récupérer tous les festivals
  getAll: async (params = {}) => {
    await delay();

    let festivals = [...mockFestivals];

    // Filtrer par participation si demandé
    if (params.participating === 'true') {
      festivals = festivals.filter(f => f.participating);
    } else if (params.participating === 'false') {
      festivals = festivals.filter(f => !f.participating);
    }

    // Recherche par titre
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      festivals = festivals.filter(f =>
        f.title.toLowerCase().includes(searchLower) ||
        f.description.toLowerCase().includes(searchLower)
      );
    }

    return {
      success: true,
      data: festivals,
      total: festivals.length
    };
  },

  // Récupérer un festival par ID
  getById: async (id) => {
    await delay();
    const festival = mockFestivals.find(f => f.id === parseInt(id));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    return {
      success: true,
      data: festival
    };
  },

  // Créer un festival
  create: async (festivalData) => {
    await delay();

    const newFestival = {
      ...festivalData,
      id: nextId++,
      subEvents: festivalData.subEvents || []
    };

    mockFestivals.push(newFestival);

    return {
      success: true,
      data: newFestival,
      message: 'Festival créé avec succès'
    };
  },

  // Mettre à jour un festival
  update: async (id, festivalData) => {
    await delay();

    const index = mockFestivals.findIndex(f => f.id === parseInt(id));

    if (index === -1) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    mockFestivals[index] = {
      ...mockFestivals[index],
      ...festivalData,
      id: parseInt(id), // Garder l'ID original
      subEvents: mockFestivals[index].subEvents // Garder les tournois
    };

    return {
      success: true,
      data: mockFestivals[index],
      message: 'Festival modifié avec succès'
    };
  },

  // Supprimer un festival
  delete: async (id) => {
    await delay();

    const index = mockFestivals.findIndex(f => f.id === parseInt(id));

    if (index === -1) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    mockFestivals.splice(index, 1);

    return {
      success: true,
      message: 'Festival supprimé avec succès'
    };
  },

  // Récupérer les tournois d'un festival
  getTournaments: async (festivalId) => {
    await delay();

    const festival = mockFestivals.find(f => f.id === parseInt(festivalId));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    return {
      success: true,
      data: festival.subEvents
    };
  },
};

// =============================================
// MOCK TOURNAMENTS API
// =============================================

export const mockTournamentsAPI = {
  // Récupérer tous les tournois
  getAll: async (params = {}) => {
    await delay();

    const allTournaments = mockFestivals.flatMap(festival =>
      festival.subEvents.map(tournament => ({
        ...tournament,
        festivalId: festival.id,
        festivalTitle: festival.title
      }))
    );

    return {
      success: true,
      data: allTournaments,
      total: allTournaments.length
    };
  },

  // Récupérer un tournoi par ID
  getById: async (festivalId, tournamentId) => {
    await delay();

    const festival = mockFestivals.find(f => f.id === parseInt(festivalId));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    const tournament = festival.subEvents.find(t => t.id === parseInt(tournamentId));

    if (!tournament) {
      throw new MockApiError(404, 'Tournoi non trouvé');
    }

    return {
      success: true,
      data: tournament
    };
  },

  // Créer un tournoi
  create: async (festivalId, tournamentData) => {
    await delay();

    const festival = mockFestivals.find(f => f.id === parseInt(festivalId));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    const newTournament = {
      ...tournamentData,
      id: nextTournamentId++
    };

    festival.subEvents.push(newTournament);

    return {
      success: true,
      data: newTournament,
      message: 'Tournoi créé avec succès'
    };
  },

  // Mettre à jour un tournoi
  update: async (festivalId, tournamentId, tournamentData) => {
    await delay();

    const festival = mockFestivals.find(f => f.id === parseInt(festivalId));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    const index = festival.subEvents.findIndex(t => t.id === parseInt(tournamentId));

    if (index === -1) {
      throw new MockApiError(404, 'Tournoi non trouvé');
    }

    festival.subEvents[index] = {
      ...festival.subEvents[index],
      ...tournamentData,
      id: parseInt(tournamentId) // Garder l'ID original
    };

    return {
      success: true,
      data: festival.subEvents[index],
      message: 'Tournoi modifié avec succès'
    };
  },

  // Supprimer un tournoi
  delete: async (festivalId, tournamentId) => {
    await delay();

    const festival = mockFestivals.find(f => f.id === parseInt(festivalId));

    if (!festival) {
      throw new MockApiError(404, 'Festival non trouvé');
    }

    const index = festival.subEvents.findIndex(t => t.id === parseInt(tournamentId));

    if (index === -1) {
      throw new MockApiError(404, 'Tournoi non trouvé');
    }

    festival.subEvents.splice(index, 1);

    return {
      success: true,
      message: 'Tournoi supprimé avec succès'
    };
  },
};

// Fonction pour réinitialiser les données mock (utile pour les tests)
export const resetMockData = () => {
  mockFestivals = [...initialEvents];
  nextId = Math.max(...mockFestivals.map(f => f.id)) + 1;
  nextTournamentId = Math.max(
    ...mockFestivals.flatMap(f => f.subEvents.map(t => t.id))
  ) + 1;
};